import {
  Box, Button, Form, FormField, Input, Modal, SpaceBetween, Textarea,
} from "@cloudscape-design/components";
import { useEffect, useState } from "react";

// A reusable { title; url } row shared by Jira epics, Sciforma links and External links.
interface LinkRow {
  title: string;
  url: string;
}

// Retained alias for the Jira epics list to keep call sites readable.
type EpicRow = LinkRow;

interface ProjectFormModalProps {
  visible: boolean;
  mode: "create" | "edit";
  saving?: boolean;
  initial?: {
    name?: string;
    description?: string;
    epics?: EpicRow[];
    sciforma_links?: LinkRow[];
    external_links?: LinkRow[];
  };
  onDismiss: () => void;
  onSubmit: (values: {
    name: string;
    description: string;
    jira_epics: EpicRow[];
    sciforma_links: LinkRow[];
    external_links: LinkRow[];
  }) => void;
}

/**
 * Shared project create/edit modal. Intentionally has NO account field — account is
 * not meaningful on a project. Manages its own field state, which is (re)seeded from
 * `initial` each time the modal becomes visible.
 */
export default function ProjectFormModal({
  visible,
  mode,
  saving = false,
  initial,
  onDismiss,
  onSubmit,
}: ProjectFormModalProps) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [epics, setEpics] = useState<EpicRow[]>([]);
  const [sciformaLinks, setSciformaLinks] = useState<LinkRow[]>([]);
  const [externalLinks, setExternalLinks] = useState<LinkRow[]>([]);

  // Seed internal state from `initial` whenever the modal transitions to visible.
  useEffect(() => {
    if (visible) {
      setName(initial?.name ?? "");
      setDescription(initial?.description ?? "");
      setEpics((initial?.epics ?? []).map((e) => ({ title: e.title ?? "", url: e.url ?? "" })));
      setSciformaLinks((initial?.sciforma_links ?? []).map((l) => ({ title: l.title ?? "", url: l.url ?? "" })));
      setExternalLinks((initial?.external_links ?? []).map((l) => ({ title: l.title ?? "", url: l.url ?? "" })));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  // Trim each row and drop rows where both title and url are empty.
  const cleanLinks = (rows: LinkRow[]): LinkRow[] =>
    rows
      .map((r) => ({ title: r.title.trim(), url: r.url.trim() }))
      .filter((r) => r.title !== "" || r.url !== "");

  const handleSubmit = () => {
    onSubmit({
      name,
      description,
      jira_epics: cleanLinks(epics),
      sciforma_links: cleanLinks(sciformaLinks),
      external_links: cleanLinks(externalLinks),
    });
  };

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header={mode === "edit" ? "Edit project" : "Create project"}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss}>Cancel</Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              loading={saving}
              disabled={name.trim() === ""}
            >
              Submit
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <Form>
        <SpaceBetween size="m">
          <FormField label="Name" description="A unique, human-readable name for the project.">
            <Input value={name} onChange={({ detail }) => setName(detail.value)} />
          </FormField>
          <FormField
            label="Description"
            description="Optional free-text description of the project (purpose, ownership, notes)."
          >
            <Textarea
              value={description}
              onChange={({ detail }) => setDescription(detail.value)}
              placeholder="Describe this project…"
              rows={3}
            />
          </FormField>
          <FormField
            label="Jira epics"
            description="Optional links to related Jira epics. Each epic needs a title and a URL."
          >
            <SpaceBetween size="xs">
              {epics.map((epic, index) => (
                <div key={`epic-${index}`}>
                  <SpaceBetween direction="horizontal" size="xs">
                    <Input
                      value={epic.title}
                      placeholder="Title"
                      onChange={({ detail }) =>
                        setEpics((prev) => prev.map((e, i) => (i === index ? { ...e, title: detail.value } : e)))
                      }
                    />
                    <Input
                      value={epic.url}
                      placeholder="https://jira.example.com/browse/EPIC-123"
                      onChange={({ detail }) =>
                        setEpics((prev) => prev.map((e, i) => (i === index ? { ...e, url: detail.value } : e)))
                      }
                    />
                    <Button
                      iconName="remove"
                      variant="icon"
                      ariaLabel={`Remove epic ${index + 1}`}
                      onClick={() => setEpics((prev) => prev.filter((_, i) => i !== index))}
                    />
                  </SpaceBetween>
                </div>
              ))}
              <Button iconName="add-plus" onClick={() => setEpics((prev) => [...prev, { title: "", url: "" }])}>
                Add epic
              </Button>
            </SpaceBetween>
          </FormField>
          <FormField
            label="Sciforma links"
            description="Optional links to related Sciforma items. Each link needs a title and a URL."
          >
            <SpaceBetween size="xs">
              {sciformaLinks.map((link, index) => (
                <div key={`sciforma-${index}`}>
                  <SpaceBetween direction="horizontal" size="xs">
                    <Input
                      value={link.title}
                      placeholder="Title"
                      onChange={({ detail }) =>
                        setSciformaLinks((prev) => prev.map((l, i) => (i === index ? { ...l, title: detail.value } : l)))
                      }
                    />
                    <Input
                      value={link.url}
                      placeholder="https://sciforma.example.com/project/123"
                      onChange={({ detail }) =>
                        setSciformaLinks((prev) => prev.map((l, i) => (i === index ? { ...l, url: detail.value } : l)))
                      }
                    />
                    <Button
                      iconName="remove"
                      variant="icon"
                      ariaLabel={`Remove Sciforma link ${index + 1}`}
                      onClick={() => setSciformaLinks((prev) => prev.filter((_, i) => i !== index))}
                    />
                  </SpaceBetween>
                </div>
              ))}
              <Button iconName="add-plus" onClick={() => setSciformaLinks((prev) => [...prev, { title: "", url: "" }])}>
                Add Sciforma link
              </Button>
            </SpaceBetween>
          </FormField>
          <FormField
            label="External links"
            description="Optional links to any other related external resources. Each link needs a title and a URL."
          >
            <SpaceBetween size="xs">
              {externalLinks.map((link, index) => (
                <div key={`external-${index}`}>
                  <SpaceBetween direction="horizontal" size="xs">
                    <Input
                      value={link.title}
                      placeholder="Title"
                      onChange={({ detail }) =>
                        setExternalLinks((prev) => prev.map((l, i) => (i === index ? { ...l, title: detail.value } : l)))
                      }
                    />
                    <Input
                      value={link.url}
                      placeholder="https://example.com/resource"
                      onChange={({ detail }) =>
                        setExternalLinks((prev) => prev.map((l, i) => (i === index ? { ...l, url: detail.value } : l)))
                      }
                    />
                    <Button
                      iconName="remove"
                      variant="icon"
                      ariaLabel={`Remove external link ${index + 1}`}
                      onClick={() => setExternalLinks((prev) => prev.filter((_, i) => i !== index))}
                    />
                  </SpaceBetween>
                </div>
              ))}
              <Button iconName="add-plus" onClick={() => setExternalLinks((prev) => [...prev, { title: "", url: "" }])}>
                Add external link
              </Button>
            </SpaceBetween>
          </FormField>
        </SpaceBetween>
      </Form>
    </Modal>
  );
}
